import { createContext, useEffect, useReducer } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import type { User } from 'src/_types/user';
import { isValidToken, setSession } from 'src/utils/jwt';
import { IdentityServer } from 'src/clients/rest';
import { IDRGraphQLClient } from 'src/clients/graphql';
import { userProfileQuery } from 'src/graphql/idr/user/queries/userProfile';

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
}

interface AuthContextValue extends State {
  platform: 'JWT';
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => Promise<void>;
  register: (username: string, name: string, password: string) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: User;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
  payload: {
    user: User;
  };
};

type Action = InitializeAction | LoginAction | LogoutAction | RegisterAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state: State, action: RegisterAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state: State, action: Action): State =>
  (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  initialize: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = async (): Promise<void> => {
    try {
      const accessToken =
      localStorage.getItem('accessToken') ||
      sessionStorage.getItem('accessToken');

      const refreshToken =
      localStorage.getItem('refreshToken');

      if (accessToken && isValidToken(accessToken)) {
        try {
          setSession(accessToken, refreshToken);
          await IdentityServer.get('/connect/userinfo');
          const { userQueries } = await IDRGraphQLClient.request(userProfileQuery, { data: {} });
          const user = userQueries.profile.listDto.items[0] as User;

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user
            },
          });
        } catch (err) {
          try {
          // try refresh if fails

          } catch (err2) {
            console.error(err2);
            setSession(null);
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
          }
        }
      } else {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const login = async (username: string, password: string, remember?: boolean): Promise<void> => {
    const loginData = new URLSearchParams();
    loginData.append('username', username);
    loginData.append('password', password);
    loginData.append('grant_type', 'password');
    loginData.append('scope', 'offline_access openid profile');
    loginData.append('client_id', process.env.REACT_APP_CLIENT_ID,);
    loginData.append('client_secret', process.env.REACT_APP_CLIENT_SECRET);

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { data: { access_token, refresh_token } } = await IdentityServer.post(
      '/connect/token',
      loginData,
      { headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      } }
    );

    setSession(access_token, refresh_token, remember);
    const { userQueries } = await IDRGraphQLClient.request(userProfileQuery, { data: {} });
    const user = userQueries.profile.listDto.items[0] as User;

    dispatch({
      type: 'LOGIN',
      payload: {
        user
      },
    });
  };

  const logout = async (): Promise<void> => {
    localStorage.removeItem('accessToken');
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (
    username: string,
    name: string,
    password: string,
  ): Promise<void> => {
    // const accessToken = await authApi.register({ username, name, password });
    // const user = await authApi.me(accessToken);

    console.log(username, name, password);

    setSession(null);

    dispatch({
      type: 'REGISTER',
      payload: {
        user: null,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        logout,
        register,
        initialize
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
